<script setup lang="ts">
import type { Options } from "@/types";

import MdcChip from "@/components/Mdc/MdcChip.vue";

interface Props {
    list: Options[];
    activeChips?: null | string | string[] | number | number[];
    setRole?: string;
    chipsRole?: string;
    justifyContent?: "center" | "flex-start" | "flex-end" | "space-between";
    alignItems?: "center" | "flex-start" | "flex-end";
}

const props = withDefaults(defineProps<Props>(), {
    activeChips: null,
    setRole: "grid",
    chipsRole: "presentation",
    justifyContent: "center",
    alignItems: "center",
});

const emit = defineEmits<{
    (e: "update", id: string | string[] | number | number[]): void;
}>();

function emitUpdate(val: string | number): void {
    if (Array.isArray(props.activeChips)) {
        let res = props.activeChips.slice(0) as string[] | number[];
        let valIndex = res.indexOf(val as never);
        valIndex === -1 ? res.push(val as never) : res.splice(valIndex, 1);
        emit("update", res);
    } else {
        emit("update", val);
    }
}
</script>

<template>
    <span class="mdc-evolution-chip-set" :role="props.setRole">
        <span
            class="mdc-evolution-chip-set__chips"
            :role="props.chipsRole"
            :class="{
                'justify-content-start': props.justifyContent === 'flex-start',
                'justify-content-end': props.justifyContent === 'flex-end',
                'justify-content-between':
                    props.justifyContent === 'space-between',
                'align-items-start': props.alignItems === 'flex-start',
                'align-items-end': props.alignItems === 'flex-end',
            }"
        >
            <MdcChip
                v-if="props.list"
                v-for="(chip, index) in props.list"
                :key="index"
                :label="chip.label"
                :value="chip.value"
                :active="
                    props.activeChips === null
                        ? null
                        : Array.isArray(props.activeChips)
                        ? props.activeChips.indexOf(chip.value) !== -1
                        : props.activeChips === chip.value
                "
                @click="emitUpdate(chip.value)"
            />
            <slot />
        </span>
    </span>
</template>

<style scoped lang="scss">
.mdc-evolution-chip-set__chips {
    justify-content: center;
    align-items: center;
    width: 100%;
    gap: 12px;
    &.justify-content-start {
        justify-content: flex-start;
    }
    &.justify-content-end {
        justify-content: flex-end;
    }
    &.justify-content-between {
        justify-content: space-between;
    }
    &.align-items-start {
        align-items: flex-start;
    }
    &.align-items-end {
        align-items: flex-end;
    }
}
</style>
